import { notification } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { globalAppConfig } from "../../config";
import CalendarEventsFilterInfo from "../../context/calendarEventsFilterInfo";
import Helper from "../../Helper";
import { CalendarEvent } from "../../models/CalendarEvent";
import { CalendarEventResource } from "../../models/CalendarEventResource";
import { plotFetch } from "../Helper/ApiRequest";

// Never rejects. A request, HTTP or parsing failure resolves to no events with `failed: true`, so callers that need to
// tell "couldn't load" from "there are none" can check it; callers that don't can keep ignoring it.
export function getCalendarEvents(
    teamSubdomain: string,
    projectToken: string,
    filterInfo: CalendarEventsFilterInfo,
    timezone: string
): Promise<{ total_count: number; calendar_events: CalendarEvent[]; failed: boolean }> {
    const url = new URL(`${Helper.baseUrlAltSubdomain(teamSubdomain)}/projects/${projectToken}/calendar_event_query`);

    return plotFetch(
        url.toString(),
        {
            method: "POST",
            body: JSON.stringify(filterInfo.toApiObject()),
        },
        true
    )
        .then((response) => {
            if (response.ok) {
                return response.json();
            }

            throw new Error(`Calendar event query failed with HTTP ${response.status}`);
        })
        .then((response) => {
            return {
                total_count: response.total_count,
                calendar_events: response.calendar_events.map((calendar_event: any) =>
                    CalendarEvent.fromApiObject(calendar_event, timezone)
                ),
                failed: false,
            };
        })
        .catch(() => {
            return { total_count: 0, calendar_events: [], failed: true };
        });
}

export function getCalendarEvent(
    teamSubdomain: string,
    projectToken: string,
    calendarEventId: number,
    timezone: string
): Promise<CalendarEvent | null> {
    const url = new URL(
        `${Helper.baseUrlAltSubdomain(teamSubdomain)}/projects/${projectToken}/calendar_events/${calendarEventId}`
    );

    return plotFetch(url.toString())
        .then((response) => {
            if (response.ok) {
                return response.json();
            }

            throw new Error("Failed to get calendar event");
        })
        .then((response) => {
            return CalendarEvent.fromApiObject(response, timezone);
        })
        .catch(() => {
            return null;
        });
}

export function updateCalendarEvent(
    teamSubdomain: string,
    projectToken: string,
    calendarEvent: CalendarEvent,
    subdomain: string
): Promise<CalendarEvent> {
    const url = new URL(
        `${Helper.baseUrlAltSubdomain(teamSubdomain)}/projects/${projectToken}/calendar_events/${calendarEvent.id}`
    );

    return plotFetch(url.toString(), {
        method: "PUT",
        body: JSON.stringify({ calendar_event: calendarEvent.toApiObject() }),
    })
        .then((response) => {
            if (response.ok) {
                return response.json();
            }

            throw new Error("Failed to update calendar event");
        })
        .then((response) => {
            return CalendarEvent.fromApiObject(response, subdomain);
        });
}

export function createCalendarEvent(
    teamSubdomain: string,
    projectToken: string,
    calendarEvent: CalendarEvent,
    subdomain: string
): Promise<CalendarEvent> {
    const url = new URL(`${Helper.baseUrlAltSubdomain(teamSubdomain)}/projects/${projectToken}/calendar_events`);

    return plotFetch(url.toString(), {
        method: "POST",
        body: JSON.stringify({ calendar_event: calendarEvent.toApiObject() }),
    })
        .then((response) => {
            if (response.ok) {
                return response.json();
            }

            throw new Error("Failed to create calendar event");
        })
        .then((response) => {
            return CalendarEvent.fromApiObject(response, subdomain);
        });
}

export function deleteCalendarEvent(
    teamSubdomain: string,
    projectToken: string,
    calendarEvent: CalendarEvent
): Promise<boolean> {
    const url = new URL(
        `${Helper.baseUrlAltSubdomain(teamSubdomain)}/projects/${projectToken}/calendar_events/${calendarEvent.id}`
    );

    return plotFetch(url.toString(), { method: "DELETE" }, true)
        .then((data) => {
            if (data.ok) {
                notification.success({
                    message: "Calendar Event Archived",
                    description: "The calendar event has been archived successfully.",
                    duration: 2,
                });
                return true;
            }
            throw new Error("Network error.");
        })
        .catch(() => {
            return false;
        });
}

export function finalApproveCalendarEvent(
    teamSubdomain: string,
    projectToken: string,
    calendarEvent: CalendarEvent,
    finalApproved: boolean
): Promise<boolean> {
    const url = new URL(
        `${Helper.baseUrlAltSubdomain(teamSubdomain)}/projects/${projectToken}/calendar_events/${calendarEvent.id}/final_approvals`
    );

    return plotFetch(url.toString(), {
        method: "POST",
        body: JSON.stringify({ final_approved: finalApproved }),
    })
        .then((response) => {
            if (response.ok) {
                // notification.success({
                //     message: "Final Approval Updated",
                //     description: "The final approval status has been updated successfully.",
                //     duration: 2,
                // });
                return true;
            }

            throw new Error("Failed to update final approval status");
        })
        .catch(() => {
            return false;
        });
}

export function approveCalendarEventResource(
    teamSubdomain: string,
    projectToken: string,
    calendarEvent: CalendarEvent,
    resource: CalendarEventResource
): Promise<boolean> {
    const url = new URL(
        `${Helper.baseUrlAltSubdomain(teamSubdomain)}/projects/${projectToken}/calendar_events/${calendarEvent.id}/resource_approvals/${resource.id}`
    );

    return plotFetch(url.toString(), {
        method: "PUT",
        body: JSON.stringify({ participation_status: resource.participationStatus }),
    })
        .then((response) => {
            if (response.ok) {
                // notification.success({
                //     message: "Resource Approval Updated",
                //     description: "The resource approval status has been updated successfully.",
                //     duration: 2,
                // });
                return true;
            }

            throw new Error("Failed to update approval status");
        })
        .catch(() => {
            return false;
        });
}

export function getCalendarEventConflicts(
    teamSubdomain: string,
    projectToken: string,
    calendarEvent: CalendarEvent,
    timezone: string
): Promise<{ available: boolean; conflicts: CalendarEvent[]; openings: CalendarEvent[] }> {
    const url = new URL(
        `${Helper.baseUrlAltSubdomain(teamSubdomain)}/projects/${projectToken}/calendar_event_conflicts`
    );

    return plotFetch(url.toString(), {
        method: "POST",
        body: JSON.stringify({ calendar_event: calendarEvent.toApiObject() }),
    })
        .then((response) => {
            if (response.ok) {
                return response.json();
            }

            throw new Error("Failed to get calendar event conflicts");
        })
        .then((response) => {
            return {
                available: response.available,
                conflicts: response.conflicts.map((conflict: any) => CalendarEvent.fromApiObject(conflict, timezone)),
                openings: response.openings.map((opening: any) => CalendarEvent.fromApiObject(opening, timezone)),
            };
        })
        .catch(() => {
            return {
                available: false,
                conflicts: [],
                openings: [],
            };
        });
}

export function getCalendarEventAvailabilities(
    teamSubdomain: string,
    projectToken: string,
    calendarEvent: CalendarEvent,
    timezone: string
): Promise<Dayjs[]> {
    const url = new URL(
        `${Helper.baseUrlAltSubdomain(teamSubdomain)}/projects/${projectToken}/calendar_event_availabilities`
    );

    return plotFetch(url.toString(), {
        method: "POST",
        body: JSON.stringify({ calendar_event: calendarEvent.toApiObject() }),
    })
        .then((response) => {
            if (response.ok) {
                return response.json();
            }

            throw new Error("Failed to get calendar event availabilities");
        })
        .then((response) => {
            return response.availabilities.map((availability: any) => dayjs(availability).tz(timezone));
        })
        .catch(() => {
            return [];
        });
}

export function reassignDeliveries(
    teamSubdomain: string,
    projectToken: string,
    itemToReplace: { mappable_type: string; mappable_id: number },
    reassignmentParams: {
        calendar_event_id: number;
        new_drop_off_id?: number | null;
        new_equipment_id?: number | null;
        new_gate_id?: number | null;
        new_haul_route_id?: number | null;
        should_archive: boolean;
    }[]
): Promise<boolean> {
    const url = new URL(`${Helper.baseUrlAltSubdomain(teamSubdomain)}/projects/${projectToken}/reassignments`);

    return plotFetch(url.toString(), {
        method: "PUT",
        body: JSON.stringify({
            item_to_replace: itemToReplace,
            reassignments: reassignmentParams,
        }),
    })
        .then((response) => {
            if (response.ok) {
                notification.success({
                    message: "Deliveries Reassigned",
                    description: "The deliveries have been reassigned successfully.",
                    duration: globalAppConfig.NOTIFICATION_DURATION,
                });
                return true;
            }

            throw new Error("Failed to reassign deliveries");
        })
        .catch(() => {
            return false;
        });
}
